import React, { useState, useEffect, useRef } from 'react';
import '../styles/home.css';
import { fetchSheet } from "../utils/googleSheets";
import EnquiryForm from './EnquiryForm';
import { useNavigate, useLocation } from 'react-router-dom';

import logo from "../images/logo.png";
import showroom from "../images/showroom.png";
import white_logo from "../images/white_logo.png";

import {
  FaSearch,
  FaShoppingCart,
  FaBars,
  FaTimes,
  FaStar,
  FaStarHalfAlt,
  FaCheck,
  FaTruck,
  FaAward,
  FaUsers,
  FaCalendarAlt,
  FaTag,
  FaInstagram,
  FaFacebookF,
  FaTwitter,
  FaLinkedinIn,
  FaMapMarkerAlt,
  FaPhone,
  FaEnvelope,
  FaWhatsapp,
  FaRulerCombined,
  FaPalette,
  FaShieldAlt,
  FaHeart,
  FaEye,
  FaArrowRight,
  FaChevronLeft,
  FaChevronRight,
  FaQuoteLeft,
  FaHome,
  FaBath,
  FaBed,
  FaBuilding,
  FaFire,
  FaCube,
  FaPaintBrush,
  FaUtensils,
  FaCouch,
  FaTree,
  FaWarehouse,
  FaTint,
  FaThermometerHalf,
  FaExpand,
  FaFilter
} from 'react-icons/fa';

import { FaStairs } from 'react-icons/fa6';

const getDriveImageUrl = (url) => {
  if (!url) return null;

  // Already non-Drive image (Unsplash, CDN, etc.)
  if (!url.includes("drive.google.com")) return url;

  let fileId = "";

  // /file/d/FILE_ID
  const match1 = url.match(/\/file\/d\/([^/]+)/);

  // ?id=FILE_ID
  const match2 = url.match(/[?&]id=([^&]+)/);

  if (match1) {
    fileId = match1[1];
  } else if (match2) {
    fileId = match2[1];
  }

  if (!fileId) return null;

  // Google Drive thumbnail endpoint (HOTLINK SAFE)
  return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
};

// Helper functions for icons
const getTileTypeIcon = (type) => {
  if (!type) return <FaPalette />;
  const typeLower = type.toLowerCase();
  if (typeLower.includes('floor')) return <FaHome />;
  if (typeLower.includes('wall')) return <FaPalette />;
  if (typeLower.includes('appliance')) return <FaThermometerHalf />;
  if (typeLower.includes('countertop')) return <FaExpand />;
  if (typeLower.includes('elevation')) return <FaBuilding />;
  return <FaPalette />;
};

const getApplicationIcon = (app) => {
  if (!app) return <FaHome />;
  const appLower = app.toLowerCase();
  if (appLower.includes('bathroom')) return <FaBath />;
  if (appLower.includes('kitchen')) return <FaUtensils />;
  if (appLower.includes('living')) return <FaCouch />;
  if (appLower.includes('bedroom')) return <FaBed />;
  if (appLower.includes('outdoor')) return <FaTree />;
  if (appLower.includes('commercial')) return <FaWarehouse />;
  if (appLower.includes('staircase')) return <FaStairs />;
  if (appLower.includes('water')) return <FaTint />;
  return <FaHome />;
};

const getBadgeColor = (badge) => {
  if (!badge) return '';
  const badgeLower = badge.toLowerCase();
  if (badgeLower.includes('bestseller')) return 'bestseller';
  if (badgeLower.includes('trending')) return 'trending';
  if (badgeLower.includes('premium')) return 'premium';
  if (badgeLower.includes('new')) return 'new';
  if (badgeLower.includes('featured')) return 'featured';
  if (badgeLower.includes('sale')) return 'sale';
  return '';
};

const getBadgeIcon = (badge) => {
  if (!badge) return null;
  const badgeLower = badge.toLowerCase();
  if (badgeLower.includes('bestseller') || badgeLower.includes('trending')) return <FaFire />;
  if (badgeLower.includes('new')) return <FaTag />;
  return null;
};

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [cartCount, setCartCount] = useState(3);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const testimonialSliderRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);

  // Quick View Modal State
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const heroSlides = [
    {
      image:
        "https://images.unsplash.com/photo-1762856490803-8e200418973a?q=80&w=1160&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
      , title: "Crafting Spaces\nThat Last",
      subtitle: "Quality tiles backed by years of expertise."
    },
    {
      image:
        "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=80&w=1600&auto=format&fit=crop",
      title: "Design That\nFeels Like Home",
      subtitle: "Textures and finishes crafted for modern living."
    },
    {
      image:
        "https://images.unsplash.com/photo-1600210492493-0946911123ea?q=80&w=1600&auto=format&fit=crop",
      title: "Luxury You\nCan Walk On",
      subtitle: "Premium tiles designed to last generations."
    },
    {
      image:
        "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?q=80&w=1600&auto=format&fit=crop",
      title: "Built For\nInspired Spaces",
      subtitle: "Where durability meets elegant design."
    }
  ];

  const stats = [
    {
      id: 1,
      number: "5000+",
      label: "Satisfied Customers",
      icon: <FaUsers />,
      description: "Happy clients across India"
    },
    {
      id: 2,
      number: "25+",
      label: "Years Experience",
      icon: <FaAward />,
      description: "Trusted since 2001"
    },
    {
      id: 3,
      number: "50,000+",
      label: "Products Available",
      icon: <FaBuilding />,
      description: "Wide variety of tiles"
    }
  ];

  const [currentSlide, setCurrentSlide] = useState(0);

  // Auto-slide every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 10000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [
          categoriesData,
          productsData,
          testimonialsData,
          blogData
        ] = await Promise.all([
          fetchSheet("Categories"),
          fetchSheet("Products"),
          fetchSheet("Testimonials"),
          fetchSheet("BlogPosts"),
        ]);

        const normalizedCategories = categoriesData.map((c) => ({
          ...c,
          image: getDriveImageUrl(c.image),
          products: Number(c.products),
          styles: Number(c.styles),
        }));

        const normalizedProducts = productsData.map((p) => ({
          ...p,
          image: getDriveImageUrl(p.image),
          price: Number(p.price),
          originalPrice: Number(p.originalPrice),
          rating: Number(p.rating),
          reviewCount: Number(p.reviewCount),
          discount: Number(p.discount),
          type: p.type || '',
          size: p.size || '',
          material: p.material || '',
          style: p.style || '',
          application: p.application || '',
          description: `High-quality ${p.material || 'ceramic'} tiles perfect for ${p.application || 'various spaces'}. ${p.style || 'Elegant'} finish with durable construction.`,
          features: [
            'Water Resistant',
            'Scratch Proof',
            'Easy to Clean',
            'Long Lasting',
            'Low Maintenance'
          ]
        }));

        const normalizedTestimonials = testimonialsData.map((t) => ({
          ...t,
          avatar: getDriveImageUrl(t.avatar),
        }));

        const normalizedBlogs = blogData.map((b) => ({
          ...b,
          image: getDriveImageUrl(b.image),
        }));

        setCategories(normalizedCategories);
        setProducts(normalizedProducts);
        setTestimonials(normalizedTestimonials);
        setBlogPosts(normalizedBlogs);
        setCurrentTestimonial(0);

      } catch (err) {
        console.error("Google Sheets fetch error:", err);
      }
    };

    loadData();
  }, []);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle scroll to section when URL has hash
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  // Auto rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
    document.body.style.overflow = !mobileMenuOpen ? 'hidden' : '';
  };

  // Close mobile menu when clicking outside
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      toggleMobileMenu();
    }
  };

  // Handle navigation link click with smooth scroll
  const handleNavLinkClick = (e, targetId) => {
    e.preventDefault();

    // Close mobile menu
    setMobileMenuOpen(false);
    document.body.style.overflow = '';

    if (targetId.startsWith('#')) {
      // For same-page navigation
      const element = document.querySelector(targetId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      // For external navigation
      window.location.href = targetId;
    }
  };

  // Scroll to specific section
  const scrollToSection = (sectionId) => {
    const element = document.querySelector(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
    document.body.style.overflow = '';
  };

  // Open Quick View Modal
  const openQuickView = (product) => {
    setQuickViewProduct(product);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  // Close Quick View Modal
  const closeQuickView = () => {
    setIsModalOpen(false);
    setQuickViewProduct(null);
    document.body.style.overflow = 'auto';
  };

  // Render star rating
  const renderRating = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<FaStar key={i} className="star" />);
    }

    if (hasHalfStar) {
      stars.push(<FaStarHalfAlt key="half" className="star" />);
    }

    const emptyStars = 5 - stars.length;
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<FaStar key={`empty-${i}`} className="star empty" />);
    }

    return stars;
  };

  const handleEnquireNow = (e) => {
    e.preventDefault();
    setShowEnquiryForm(true);
  };

  // Handle Enquire Now with WhatsApp redirection
  const handleWhatsAppEnquiry = (product = null) => {
    if (product) {
      const productName = product.name || 'this product';
      const message = encodeURIComponent(`Hi, I'm interested in ${productName}. Could you please provide more details?`);
      const phoneNumber = '919942169120';
      window.location.href = `https://wa.me/${phoneNumber}?text=${message}`;
      return;
    }
  };

  // NEW FUNCTION: Handle View Collection click
  const handleViewCollection = (categoryName, e) => {
    e.preventDefault();
    // Navigate to products page with category parameter
    navigate(`/products?category=${encodeURIComponent(categoryName)}`);
  };

  return (
    <>
      <EnquiryForm
        isOpen={showEnquiryForm}
        onClose={() => setShowEnquiryForm(false)}
      />

      {/* Header */}
      <header className={`main-header ${scrolled ? 'scrolled' : ''}`}>
        <div className="container header-container">
          <div className="logo-container">
            <img src={logo} alt="Logo" style={{ width: 50, height: 50 }} />
            <div className="logo-text">
              <h1>Shree Krishna Tiles</h1>
            </div>
          </div>

          <button
            className="mobile-menu-btn"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>

          <nav className={`main-nav ${mobileMenuOpen ? 'active' : ''}`}>
            <ul>
              <li><a href="#home" onClick={(e) => handleNavLinkClick(e, '#home')}>Home</a></li>
              <li><a href="#categories" onClick={(e) => handleNavLinkClick(e, '#categories')}>Categories</a></li>
              <li><a href="/products" onClick={(e) => handleNavLinkClick(e, '/products')}>Products</a></li>
              <li><a href="#about" onClick={(e) => handleNavLinkClick(e, '#about')}>About Us</a></li>
              <li><a href="#testimonials" onClick={(e) => handleNavLinkClick(e, '#testimonials')}>Testimonials</a></li>
              <li><a href="/blog" onClick={(e) => handleNavLinkClick(e, '/blog')}>Blog</a></li>
              <li><a href="/contact" onClick={(e) => handleNavLinkClick(e, '/contact')}>Contact</a></li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Enquire Now Button - Fixed Position */}
      <button className="enquire-now-btn" onClick={handleEnquireNow}>
        <span>Enquire Now</span>
        <FaArrowRight className="arrow-icon" />
      </button>

      {/* Hero Section */}
      <section id="home" className="hero-slider">
        {heroSlides.map((slide, index) => (
          <div
            key={index}
            className={`hero-slide ${index === currentSlide ? "active" : ""}`}
            style={{
              backgroundImage: `linear-gradient(
          rgba(0,0,0,0.45),
          rgba(0,0,0,0.7)
        ), url(${slide.image})`
            }}
          >
            <div className="hero-content">
              <h1>
                {slide.title.split("\n").map((line, i) => (
                  <span key={i}>
                    {line}
                    <br />
                  </span>
                ))}
              </h1>
              <p>{slide.subtitle}</p>
            </div>
          </div>
        ))}

        {/* Dots Navigation */}
        <div className="hero-dots">
          {heroSlides.map((_, index) => (
            <span
              key={index}
              className={`dot ${index === currentSlide ? "active" : ""}`}
              onClick={() => setCurrentSlide(index)}
            />
          ))}
        </div>

        {/* Cursor Navigation */}
        <div
          className="hero-nav left"
          onClick={() =>
            setCurrentSlide(
              (currentSlide - 1 + heroSlides.length) % heroSlides.length
            )
          }
        />
        <div
          className="hero-nav right"
          onClick={() =>
            setCurrentSlide((currentSlide + 1) % heroSlides.length)
          }
        />
      </section>

      {/* Featured Categories */}
      <section id="categories" className="featured-categories">
        <div className="container">
          <div className="section-title">
            <h2>Featured Categories</h2>
            <p>Browse our premium tile collections for every space and style</p>
          </div>

          <div className="categories-container">
            <button
              className="nav-arrow left-arrow"
              onClick={() => {
                const container = document.querySelector('.categories-grid');
                container.scrollBy({ left: -300, behavior: 'smooth' });
              }}
              aria-label="Previous categories"
            >
              <FaChevronLeft />
            </button>

            <div className="categories-grid">
              {categories.map((category) => (
                <div key={category.id} className="category-card">
                  <div className="category-image">
                    <img src={category.image} alt={category.name} />
                  </div>
                  <div className="category-content">
                    <h3>{category.name}</h3>
                    <p>{category.description}</p>
                    <div className="category-stats">
                      <div className="stat-item">
                        <span className="stat-number">{category.products}+</span>
                        <span className="stat-label">Products</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">{category.styles}</span>
                        <span className="stat-label">Styles</span>
                      </div>
                    </div>
                    <button
                      onClick={(e) => handleViewCollection(category.name, e)}
                      className="btn btn-secondary mt-20"
                    >
                      View Collection
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <button
              className="nav-arrow right-arrow"
              onClick={() => {
                const container = document.querySelector('.categories-grid');
                container.scrollBy({ left: 300, behavior: 'smooth' });
              }}
              aria-label="Next categories"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section id="products" className="featured-products">
        <div className="container">
          <div className="section-title">
            <h2>Featured Products</h2>
            <p>Best selling and highly rated tile collections</p>
          </div>

          <div className="products-grid">
            {products.map((product) => (
              <div key={product.id} className="product-card">
                <div className={`product-badge ${getBadgeColor(product.badge)}`}>
                  {getBadgeIcon(product.badge)}
                  {product.badge}
                </div>

                <div className="product-image">
                  <img src={product.image} alt={product.name} />
                  <div className="product-overlay">
                    <button
                      className="quick-view-btn"
                      onClick={() => openQuickView(product)}
                    >
                      <FaEye /> Quick View
                    </button>
                  </div>
                </div>

                <div className="product-info">
                  <div className="product-category">{product.category}</div>
                  <h3 className="product-title">{product.name}</h3>


                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-50">
            <a href="/products" className="btn btn-primary btn-large">
              View All Products
              <FaArrowRight />
            </a>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section">
        <div className="container">
          <div className="section-title">
            <h2>About Shree Krishna Tiles</h2>
            <p>Your trusted partner for premium tiling solutions</p>
          </div>

          <div className="about-content">
            <div className="about-text">
              <h3>25 Years of Excellence in Tile Manufacturing</h3>
              <p>
                Established in 2001, Shree Krishna Tiles has been at the forefront
                of providing high-quality tiles for residential and commercial spaces.
                Our commitment to quality and customer satisfaction has made us one
                of the most trusted tile brands in India.
              </p>
              <p>
                We source the finest materials and employ state-of-the-art
                manufacturing techniques to ensure every tile meets our rigorous
                quality standards. Our expert team is always ready to assist you
                in finding the perfect tiles for your project.
              </p>

              <div className="about-stats">
                {stats.map((stat) => (
                  <div key={stat.id} className="stat-box">
                    <div className="stat-icon">{stat.icon}</div>
                    <div className="stat-number">{stat.number}</div>
                    <div className="stat-label">{stat.label}</div>
                    <p className="stat-description">{stat.description}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="about-image">
              <img src={showroom} alt="Our Showroom" />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section id="testimonials" className="testimonials-section">
        <div className="container">
          <div className="section-title">
            <h2>What Our Customers Say</h2>
            <p>Trusted by homeowners, architects, and builders across India</p>
          </div>

          <div className="testimonial-slider" ref={testimonialSliderRef}>
            <div className="testimonial-card">
              {testimonials.length > 0 && (
                <>
                  <FaQuoteLeft className="quote-icon" />

                  <p className="testimonial-text">
                    {testimonials[currentTestimonial].text}
                  </p>

                  <div className="testimonial-author">
                    <div className="author-avatar">
                      <img
                        src={testimonials[currentTestimonial].avatar}
                        alt={testimonials[currentTestimonial].author}
                      />
                    </div>

                    <div className="author-info">
                      <h4>{testimonials[currentTestimonial].author}</h4>
                      <p className="author-role">
                        {testimonials[currentTestimonial].role}
                      </p>
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="slider-nav">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  className={`slider-dot ${index === currentTestimonial ? 'active' : ''}`}
                  onClick={() => setCurrentTestimonial(index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section id="blog" className="blog-section">
        <div className="container">
          <div className="section-title">
            <h2>Latest From Our Blog</h2>
            <p>Tips, trends, and inspiration for your next project</p>
          </div>

          <div className="blog-grid">
            {[...blogPosts]
              .sort((a, b) => new Date(b.publish_date) - new Date(a.publish_date))
              .slice(0, 3)
              .map((post) => (
                <article key={post.id} className="blog-card">
                  <div className="blog-image">
                    <img src={post.featured_image} alt={post.title} />
                  </div>

                  <div className="blog-content">
                    <div className="blog-meta">
                      <span className="blog-date">
                        <FaCalendarAlt /> {post.publish_date}
                      </span>
                      <span className="blog-category">
                        <FaTag /> {post.category}
                      </span>
                    </div>

                    <h3>{post.title}</h3>
                    <p>{post.excerpt}</p>

                    <a href={`/blog/${post.id}`} className="read-more">
                      Read More <FaArrowRight />
                    </a>
                  </div>
                </article>
              ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="cta-section">
        <div className="container">
          <h2>Ready to Transform Your Space?</h2>
          <p>
            Schedule a free consultation with our tile experts. We'll help you
            choose the perfect tiles for your home or commercial project.
          </p>

          <div className="cta-buttons">
            <a href="tel:+919942169120" target='_blank' className="btn btn-primary btn-large">
              <FaPhone /> Call Now
            </a>
            <a href="https://wa.me/919942169120" target='_blank' className="btn btn-secondary btn-large">
              <FaWhatsapp /> WhatsApp
            </a>
            <a href="mailto:info@shreekrishnatiles.co.in" target='_blank' className="btn btn-secondary btn-large">
              <FaEnvelope /> Email Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="main-footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-col">
              <div className="logo-container">
                <img src={white_logo} alt="SKT Logo" width="60" height="60" />
                <div>
                  <h3>Shree Krishna Tiles</h3>
                  <p>Premium Tiles Since 2001</p>
                </div>
              </div>
              <p>
                Your trusted partner for premium quality tiles.
                We provide the finest ceramic, porcelain, vitrified,
                and natural stone tiles for residential and commercial spaces.
              </p>

              <div className="social-links">
                <a href="#" className="social-link"><FaFacebookF /></a>
                <a href="#" className="social-link"><FaInstagram /></a>
                <a href="#" className="social-link"><FaTwitter /></a>
                <a href="#" className="social-link"><FaLinkedinIn /></a>
              </div>
            </div>

            <div className="footer-col">
              <h3>Quick Links</h3>
              <ul className="quick-links">
                <li><a href="/" onClick={(e) => handleNavLinkClick(e, '/')}><FaHome /> Home</a></li>
                <li><a href="/products" onClick={(e) => handleNavLinkClick(e, '/products')}><FaBath /> Products</a></li>
                <li><a href="#categories" onClick={(e) => handleNavLinkClick(e, '#categories')}><FaBed /> Categories</a></li>
                <li><a href="#about" onClick={(e) => handleNavLinkClick(e, '#about')}><FaBuilding /> About Us</a></li>
                <li><a href="#testimonials" onClick={(e) => handleNavLinkClick(e, '#testimonials')}><FaUsers /> Testimonials</a></li>
                <li><a href="/blog" onClick={(e) => handleNavLinkClick(e, '/blog')}><FaCalendarAlt /> Blog</a></li>
              </ul>
            </div>

            <div className="footer-col">
              <h3>Contact Info</h3>
              <ul className="contact-info">
                <li>
                  <div className="contact-icon">
                    <FaMapMarkerAlt />
                  </div>
                  <div>
                    <strong>Showroom Address</strong>
                    <p>4/486, Sungam, Nanjiyampalayam<br />Dharapuram, Tiruppur 638657</p>
                  </div>
                </li>
                <li>
                  <div className="contact-icon">
                    <FaPhone />
                  </div>
                  <div>
                    <strong>Phone Number</strong>
                    <p>+91 99421 69120</p>
                  </div>
                </li>
                <li>
                  <div className="contact-icon">
                    <FaEnvelope />
                  </div>
                  <div>
                    <strong>Email Address</strong>
                    <p>info@shreekrishnatiles.co.in</p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} Shree Krishna Tiles. All rights reserved.</p>
            <p>Made with ❤️ by <a href="https://theternos.github.io/portfolio/" target="_blank" rel="noopener noreferrer"> Ternos</a></p>
          </div>
        </div>
      </footer>

      {/* Quick View Modal */}
      {isModalOpen && quickViewProduct && (
        <div className="modal-overlay" onClick={closeQuickView}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeQuickView}>
              <FaTimes />
            </button>

            <div className="modal-body">
              {/* Product Image */}
              <div className="modal-image-container">
                <img src={quickViewProduct.image} alt={quickViewProduct.name} />
                {quickViewProduct.badge && (
                  <div className={`modal-badge ${getBadgeColor(quickViewProduct.badge)}`}>
                    {getBadgeIcon(quickViewProduct.badge)}
                    {quickViewProduct.badge}
                  </div>
                )}

                <div className="modal-contact mobile-view">
                  <h3>Get Professional Consultation</h3>
                  <div className="contact-options">
                    <button
                      className="btn btn-primary modal-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWhatsAppEnquiry(quickViewProduct);
                      }}
                    >
                      <FaWhatsapp /> WhatsApp Enquiry
                    </button>
                    <a
                      href="tel:+919942169120"
                      className="btn btn-secondary modal-btn"
                    >
                      <FaPhone /> Call Now
                    </a>
                  </div>
                </div>
              </div>

              {/* Product Details */}
              <div className="modal-details">
                <div className="modal-header">
                  <h2>{quickViewProduct.name}</h2>
                  <p className="modal-category">{quickViewProduct.category}</p>
                </div>

                <div className="modal-description">
                  <p>{quickViewProduct.description}</p>
                </div>

                {/* Product Specifications */}
                <div className="modal-specs">
                  <h3>Product Specifications</h3>
                  <div className="specs-grid">
                    {quickViewProduct.type && (
                      <div className="spec-item">
                        <span className="spec-label">
                          <FaPalette /> Type
                        </span>
                        <span className="spec-value">{quickViewProduct.type}</span>
                      </div>
                    )}
                    {quickViewProduct.application && (
                      <div className="spec-item">
                        <span className="spec-label">
                          <FaHome /> Application
                        </span>
                        <span className="spec-value">{quickViewProduct.application}</span>
                      </div>
                    )}
                    {quickViewProduct.size && (
                      <div className="spec-item">
                        <span className="spec-label">
                          <FaRulerCombined /> Size
                        </span>
                        <span className="spec-value">{quickViewProduct.size}</span>
                      </div>
                    )}
                    {quickViewProduct.material && (
                      <div className="spec-item">
                        <span className="spec-label">
                          <FaCube /> Material
                        </span>
                        <span className="spec-value">{quickViewProduct.material}</span>
                      </div>
                    )}
                    {quickViewProduct.style && (
                      <div className="spec-item">
                        <span className="spec-label">
                          <FaPaintBrush /> Style
                        </span>
                        <span className="spec-value">{quickViewProduct.style}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Contact Options */}
                <div className="modal-contact pc-view">
                  <h3>Get Professional Consultation</h3>
                  <div className="contact-options">
                    <button
                      className="btn btn-primary modal-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleWhatsAppEnquiry(quickViewProduct);
                      }}
                    >
                      <FaWhatsapp /> WhatsApp Enquiry
                    </button>
                    <a
                      href="tel:+919942169120"
                      className="btn btn-secondary modal-btn"
                    >
                      <FaPhone /> Call Now
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Home;